import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../pages/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const NAV = '#16213E';
const GOLD = '#D4AF37';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/vaikhanasam', label: 'Vaikhanasam' },
  { path: '/events', label: 'Events' },
];

function Navbar() {
  const { isAuthenticated, logout, role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropRef = useRef(null);

  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('vrvToken');
    const email = localStorage.getItem('vrvEmail');
    const name = localStorage.getItem('vrvName') || '';
    const surname = localStorage.getItem('vrvSurname') || '';
    if (token && email) { setUserEmail(email); setUserName(`${name} ${surname}`.trim()); }
    else { setUserEmail(''); setUserName(''); }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const outside = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  useEffect(() => {
    const handler = () => {
      const name = localStorage.getItem('vrvName') || '';
      const surname = localStorage.getItem('vrvSurname') || '';
      setUserName(`${name} ${surname}`.trim());
    };
    window.addEventListener('vrv-profile-updated', handler);
    return () => window.removeEventListener('vrv-profile-updated', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout(); setDropOpen(false); setMobileOpen(false); navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const initials = (userName || userEmail).charAt(0).toUpperCase();

  return (
    <>
      <nav
        className="fixed top-0 left-0 w-full z-50 transition-all duration-300"
        style={{
          height: '64px',
          backdropFilter: scrolled ? 'blur(14px)' : 'blur(8px)',
          WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'blur(8px)',
          background: scrolled ? 'rgba(22,33,62,0.92)' : 'rgba(15,23,42,0.85)',
          borderBottom: '1px solid rgba(212,175,55,0.12)',
          boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.2)' : 'none',
        }}
      >
        <div className="vrv-container h-full flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 no-underline">
              <img src="/images/VRV.png" alt="VRV" className="h-10 w-10 object-contain" style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))' }} />
              <span className="hidden sm:block font-bold text-sm tracking-wide" style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.9)' }}>
                Vaikhanasa Raksha Vahini
              </span>
            </Link>
          </div>

          {/* Center: Nav links (desktop) */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`relative px-4 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 ${isActive(link.path) ? 'nav-active-glow' : ''}`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  color: isActive(link.path) ? GOLD : 'rgba(255,255,255,0.65)',
                  background: isActive(link.path) ? 'rgba(212,175,55,0.1)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isActive(link.path)) { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; } }}
                onMouseLeave={e => { if (!isActive(link.path)) { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'transparent'; } }}>
                {link.label}
                {isActive(link.path) && (
                  <motion.span layoutId="nav-indicator" className="absolute bottom-0.5 left-4 right-4 h-0.5 rounded-full"
                    style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, boxShadow: `0 0 8px ${GOLD}` }} />
                )}
              </Link>
            ))}
          </div>

          {/* Right: Auth */}
          <div className="hidden md:flex items-center gap-3">
            {!isAuthenticated ? (
              <>
                <Link to="/login"
                  className="px-4 py-2 text-xs font-semibold rounded-xl transition-all duration-200"
                  style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.7)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                  Sign In
                </Link>
                <Link to="/signup" className="btn-gold text-xs px-5 py-2" style={{ minHeight: '36px', borderRadius: '10px' }}>
                  Join Us
                </Link>
              </>
            ) : (
              <div ref={dropRef} className="relative">
                <button onClick={() => setDropOpen(p => !p)}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-all duration-200"
                  style={{ background: dropOpen ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                  onMouseEnter={e => { if (!dropOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={e => { if (!dropOpen) e.currentTarget.style.background = 'transparent'; }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, #B9932F)`, color: '#0F172A', boxShadow: '0 0 12px rgba(212,175,55,0.35)' }}>
                    {initials}
                  </div>
                  <div className="flex flex-col items-start leading-tight">
                    {userName && <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{userName}</span>}
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{userEmail}</span>
                  </div>
                  <svg className={`w-3 h-3 transition-transform duration-200 ${dropOpen ? 'rotate-180' : ''}`} style={{ color: 'rgba(255,255,255,0.5)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.95 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 mt-1.5 rounded-xl overflow-hidden"
                      style={{ background: 'rgba(22,33,62,0.95)', border: '1px solid rgba(212,175,55,0.15)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>
                      <button onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 text-[11px] font-semibold tracking-wide transition-colors whitespace-nowrap"
                        style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.7)' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.background = 'transparent'; }}>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Mobile: Hamburger */}
          <button onClick={() => setMobileOpen(p => !p)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
            style={{ color: 'rgba(255,255,255,0.8)', background: mobileOpen ? 'rgba(212,175,55,0.15)' : 'transparent' }}
            aria-label="Toggle Menu">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {mobileOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed top-0 left-0 bottom-0 z-50 md:hidden w-72"
              style={{ background: `linear-gradient(160deg, ${NAV} 0%, #0F172A 100%)`, borderRight: '1px solid rgba(212,175,55,0.15)' }}>
              <div className="flex items-center justify-between px-5 h-[64px]" style={{ borderBottom: '1px solid rgba(212,175,55,0.12)' }}>
                <div className="flex items-center gap-2.5">
                  <img src="/images/VRV.png" alt="VRV" className="h-10 w-10 object-contain" style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))' }} />
                  <span className="font-bold text-sm text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Vaikhanasa Raksha Vahini</span>
                </div>
                <button onClick={() => setMobileOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/50 hover:text-white" style={{ background: 'rgba(255,255,255,0.08)' }}>&times;</button>
              </div>

              <div className="px-4 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
                {isAuthenticated && (
                  <div className="flex items-center gap-3 px-3 py-3 mb-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{ background: `linear-gradient(135deg, ${GOLD}, #B9932F)`, color: '#0F172A', fontSize: '1rem' }}>
                      {initials}
                    </div>
                    <div>
                      {userName && <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{userName}</p>}
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{userEmail}</p>
                    </div>
                  </div>
                )}

                <p className="px-3 mb-2 text-[10px] font-bold tracking-widest uppercase" style={{ color: 'rgba(212,175,55,0.5)' }}>Navigation</p>
                {navLinks.map(link => (
                  <Link key={link.path} to={link.path}
                    className="flex items-center px-3 py-3 rounded-xl mb-1 text-sm font-medium transition-all"
                    style={{ fontFamily: 'Inter, sans-serif', color: isActive(link.path) ? GOLD : 'rgba(255,255,255,0.65)', background: isActive(link.path) ? 'rgba(212,175,55,0.12)' : 'transparent' }}>
                    {link.label}
                  </Link>
                ))}

                {isAuthenticated ? (
                  <>
                    <div className="my-3 vrv-divider" />
                    <p className="px-3 mb-2 text-[10px] font-bold tracking-widest uppercase" style={{ color: 'rgba(212,175,55,0.5)' }}>My Account</p>
                    {[
                      { path: '/profile', label: 'Profile', icon: '👤' },
                      { path: '/donate', label: 'Donations', icon: '💛' },
                      { path: '/articles', label: 'Articles', icon: '📖' },
                      { path: '/youtube', label: 'Videos', icon: '▶️' },
                      ...((role || '').split(',').map(s => s.trim().toUpperCase()).some(r => ['ADMIN','CONTENT-CREATOR','CONTENT-APPROVER'].includes(r))
                        ? [{ path: '/content-management', label: 'Content', icon: '✏️' }] : []),
                    ].map(item => (
                      <Link key={item.path} to={item.path}
                        className="flex items-center gap-2.5 px-3 py-3 rounded-xl mb-1 text-sm font-medium transition-all"
                        style={{ fontFamily: 'Inter, sans-serif', color: isActive(item.path) ? GOLD : 'rgba(255,255,255,0.65)', background: isActive(item.path) ? 'rgba(212,175,55,0.12)' : 'transparent' }}>
                        <span>{item.icon}</span>{item.label}
                      </Link>
                    ))}
                    {(role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN') && (
                      <Link to="/admin"
                        className="flex items-center gap-2.5 px-3 py-3 rounded-xl mb-1 text-sm font-medium transition-all"
                        style={{ fontFamily: 'Inter, sans-serif', color: isActive('/admin') ? GOLD : 'rgba(255,255,255,0.65)', background: isActive('/admin') ? 'rgba(212,175,55,0.12)' : 'transparent' }}>
                        <span>⚙️</span> Admin
                      </Link>
                    )}
                    <div className="my-3 vrv-divider" />
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium transition-all"
                      style={{ color: '#EF4444', background: 'rgba(239,68,68,0.08)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}>
                      <span>🚪</span> Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <div className="my-3 vrv-divider" />
                    <Link to="/login" className="w-full btn-outline block text-center mb-2" style={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.2)' }}>Sign In</Link>
                    <Link to="/signup" className="w-full btn-gold block text-center">Join Us</Link>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
